import React, { useCallback, useContext, useState } from 'react';
import { Text, View, Pressable } from 'react-native';
import { useQuery, useSubscription } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import styled from 'styled-components/native';
import htmlStyled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { faClockRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import isEqual from 'react-fast-compare';

import AuthContext from 'app/src/contexts/AuthContext';
import Modal from 'app/src/elements/Modal';
import { Link } from 'app/src/utils/routing';
import {
  currency as baseCurrency,
  numberWithDelimiter
} from 'app/src/utils/stringHelpers';
import formattedLocation from 'app/src/components/Assets/formattedLocation';
import Fees from 'app/src/components/Fees';
import ResourceNotifications from 'app/src/components/Notifications/Resource';
import SaleHistory from 'app/src/components/Assets/SaleHistory';

import { SALE_ASSET_UPDATED, SALE_ASSETS } from './queries';
import BidFunctions from './BidFunctions';
import SaleAssetField from './SaleAssetField';
import './style.css';

export const currency = amount => {
  if (amount && '0.0' !== amount && '0' !== amount) {
    return baseCurrency(amount.toString().replace(/[^\d\.]/g, ''));
  } else {
    return '';
  }
};

const Cell = styled(View)`
  flex-direction: row;
  margin-left: 6px;
  margin-right: 6px;
  justify-content: space-between;
`;

const Value = styled(Text)`
  margin-left: 6px;
  margin-right: 6px;
  white-space: nowrap;
`;

const TableContainer = htmlStyled.div`
  overflow: scroll;
  height: calc(100vh - 560px);
  padding-bottom: 300px;
  position: relative;
`;

const TABLE_STYLES = {
  table: {
    backgroundColor: 'white',
  },
  header: {
    label: { fontSize: 14 }
  },
  data: {
    cell: {
      fontFamily: 'sans-serif',
      fontSize: 14,
      paddingVertical: 0,
      paddingHorizontal: 0,
    }
  },
};

const SaleProcess = ({ saleId, tableType, linkedSaleAssetId }) => {
  const { apolloClient: client, user } = useContext(AuthContext);
  const variables = {  saleId, sellerId: user?.seller.id }
  const { data } = useQuery(SALE_ASSETS, { variables });
  const [showSellerFees, setShowSellerFees] = useState(false);
  const [showSaleHistory, setShowSaleHistory] = useState(false);

  const linkedRef = useCallback(element => {
    if (element) {
      const rowIndex = data.saleAssets.findIndex(saleAsset => (
        saleAsset.id === linkedSaleAssetId
      ));

      const td = element.parentElement.parentElement;
      const tr = td.parentElement;
      const scrollable =
        td.parentElement.parentElement.parentElement.parentElement;

      scrollable.scrollTo(0, tr.offsetTop);
    }
  }, [data, linkedSaleAssetId]);

  useSubscription(SALE_ASSET_UPDATED, {
    variables,
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (!data || !data.saleAssetUpdated) return;

      const updatedSaleAsset = data.saleAssetUpdated.saleAsset;
      const { saleAssets } = client.readQuery({
        query: SALE_ASSETS, variables
      });

      const updatedIndex = saleAssets.findIndex(saleAsset => (
        updatedSaleAsset.id === saleAsset.id
      ));

      if (updatedIndex) {
        if (isEqual(saleAssets[updatedIndex], updatedSaleAsset)) {
          return;
        }
      }

      const updatedSaleAssets = [
        ...saleAssets.filter(saleAsset => (
          updatedSaleAsset.id !== saleAsset.id
        )),
      ];

      if (!updatedSaleAsset.destroyed) {
        if (updatedIndex || 0 === updatedIndex) {
          updatedSaleAssets.splice(updatedIndex, 0, updatedSaleAsset);
        } else {
          updatedSaleAssets = [...updatedSaleAssets, updatedSaleAsset];
        }
      };

      client.writeQuery({
        query: SALE_ASSETS,
        variables,
        data: { saleAssets: updatedSaleAssets },
      });
    }
  });

  const columns = [
    {
      key: 'correctedLotNumber',
      label: 'Lot #',
      sort: 'asc',
      cellStyle: {height: 28},
      value: ({ entry }) => parseInt(entry.correctedLotNumberSuffix || '0'),
      content: ({ entry }) => {
        const linkedStyle = entry.isLinked ?
          {
            backgroundColor: 'yellow',
            minHeight: 28,
            flexDirection: 'row',
            alignItems: 'center',
          } :
          {};

        return (
          <View style={{...linkedStyle}}>
            <span ref={entry.isLinked ? linkedRef : null}></span>
            <Value>{entry.correctedLotNumber}</Value>
          </View>
        );
      },
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
      value: ({ entry }) => entry.asset.stockNumber,
      content: ({ entry }) => (
        <Link to={`/assets/${entry.asset.id}`}>
          <Value>{entry.asset.stockNumber}</Value>
        </Link>
      )
    },
    {
      key: 'clientNumber',
      label: user?.seller.clientNumberLabel.replace('Number', '#'),
      value: ({ entry }) => entry.asset.clientNumber,
      content: ({ entry, value }) => (
        'pre' === tableType ? (
          <SaleAssetField
            saleAsset={entry}
            field="clientNumber"
            asset
          />
        ) : (
          <Value>{value}</Value>
        )
      )
    },
    {
      key: 'location',
      label: 'Location',
      value: ({ entry }) => formattedLocation(entry.asset),
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'year',
      label: 'Year',
      value: ({ entry }) => entry.asset.year,
      content: ({ value}) => <Value>{value}</Value>,
    },
    {
      key: 'make',
      label: 'Make',
      value: ({ entry }) => entry.asset.make,
      content: ({ value}) => <Value>{value}</Value>,
    },
    {
      key: 'model',
      label: 'Model',
      value: ({ entry }) => entry.asset.model,
      content: ({ value}) => <Value>{value}</Value>,
    },
    {
      key: 'vin',
      label: 'VIN',
      value: ({ entry }) => entry.asset.vin,
      content: ({ entry }) => <Value>{entry.asset.vin}</Value>
    },
    {
      key: 'latestTitleLog',
      label: 'Title Status',
      value: ({ entry }) => entry.asset.latestTitleLog?.status,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'inpectionUrl',
      label: 'Inspection',
      value: ({ entry }) => entry.asset.inspectionUrl,
      content: ({ value }) => (
        value && (
          <Value>
            <a href={value} target="_blank" rel="nofollow">
              {value.substr(0,12)}...
            </a>
          </Value>
        )
      )
    },
    {
      key: 'mechanicalCondition',
      label: 'Mech Condition',
      headerStyle: { maxWidth: 90 },
      value: ({ entry }) => entry.asset.mechanicalCondition,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'miles',
      label: 'Odometer',
      headerStyle: { maxWidth: 90 },
      value: ({ entry }) => entry.asset.miles,
      content: ({ value }) => (
        <Value>{value > 0 && numberWithDelimiter(value)}</Value>
      )
    },
    {
      key: 'expectedYieldPrice',
      label: 'Expected Yield Price',
      headerStyle: { maxWidth: 80 },
      value: ({ entry }) => parseFloat(entry.asset.expectedYieldPrice || 0),
      content: ({ entry, value }) => <Value>{currency(value)}</Value>,
    },
    {
      key: 'reservePrice',
      label: 'Reserve Price',
      value: ({ value }) => parseFloat(value || 0),
      content: ({ entry, value }) => (
        'pre' === tableType ? (
          <SaleAssetField
            saleAsset={entry}
            field="reservePrice"
            isCurrency
          />
        ) : (
          <Value>{currency(value)}</Value>
        )
      )
    },
    {
      key: 'price',
      label: 'Price',
      hidden: 'pre' === tableType,
      value: ({ entry }) => (
        entry.activeOffer.soldPrice || entry.activeOffer.price
      ),
      content: ({ entry, value }) => (
        (value && entry.activeOffer.isIf) ? (
          <Value>{currency(value)} - IF</Value>
        ) : (
          <Value>{currency(value)}</Value>
        )
      )
    },
    {
      key: 'bidFunctions',
      label: 'Bid Functions',
      hidden: 'pre' === tableType,
      cellStyle: { minWidth: 100 },
      value: ({ entry }) => {
        if ('Declined' === entry.activeOffer && entry.counterPrice) {
          return 'Declined (Buyer)';
        } else if (entry.activeOffer.result) {
          return entry.activeOffer.result;
        } else if (entry.activeOffer.counterPrice) {
          return `Counter ${currency(entry.activeOffer.counterPrice)}`;
        } else {
          return null;
        }
      },
      content: ({ entry, value }) => (
        (entry.activeOffer.isIf && !value && entry.activeOffer.price) ? (
          <BidFunctions
            saleAsset={entry}
            setShowSaleHistory={setShowSaleHistory}
          />
        ) : (
          <Value>{value}</Value>
        )
      )
    },
    {
      key: 'history',
      label: '',
      sort: 'prevent',
      cellStyle: { minWidth: 60 },
      content: ({ entry }) => (
        <Cell>
          <Pressable
            onPress={() => setShowSaleHistory(entry)}
            style={{display: 'inline-block'}}
          >
            <FontAwesomeIcon
              icon={faClockRotateLeft}
              size="lg"
              title="View Bid History"
            />
          </Pressable>
          {'   '}
          <ResourceNotifications resource={entry} />
        </Cell>
      )
    },
    {
      key: 'sellerFees',
      label: 'Fees',
      cellStyle: { minWidth: 100 },
      value: ({ value }) => parseFloat(value),
      content: ({ entry, value }) => (
        <Cell>
          <Text>{currency(entry.sellerFees)}</Text>
          {value > 0 && (
            <Pressable
              onPress={() => setShowSellerFees(entry)}
              style={{display: 'inline-block'}}
            >
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                title="View Fees Detail"
              />
            </Pressable>
          )}
        </Cell>
      )
    },
  ];

  const entries = (data?.saleAssets || []).map(saleAsset => {
    const isLinked = linkedSaleAssetId === saleAsset.id;
    return { ...saleAsset, isLinked };
  });

  return (
    <View>
      <TableContainer className="sales-process">
        <DataTable.Simple
          data={entries}
          columns={columns}
          pageLength="All"
          styles={TABLE_STYLES}
          htmlTable
          sortOnOrderChangeOnly
          sortOnArrowClickOnly
        />
      </TableContainer>
      {showSellerFees &&
        <Modal
          showModal={showSellerFees}
          setShowModal={setShowSellerFees}
          modalType={'Fees Detail'}
          objectName={showSellerFees.asset.stockNumber}
        >
          <View style={{margin: 10, borderBottom: '1px solid black'}}>
            <Fees asset={showSellerFees.asset} />
          </View>
        </Modal>
      }
      {showSaleHistory &&
        <Modal
          showModal={showSaleHistory}
          setShowModal={setShowSaleHistory}
          modalType="Bid History"
          objectName={showSaleHistory.asset.stockNumber}
        >
          <SaleHistory
            asset={showSaleHistory.asset}
            saleAsset={showSaleHistory}
          />
        </Modal>
      }
    </View>
  );
};

export default SaleProcess;
